import * as Popover from 'primitives/popover'
import type * as React from 'react'
import { tw } from 'utils/classnames'

const surfaceClasses = tw(
  'flex flex-col rounded-studio-geometry-semantic-radius-xl p-3',
  'border border-theme_semantic_surface_border_color-subtle',
  'bg-theme_semantic_surface_pop-over_primary-background shadow-studio-shadow-l4',
)

export interface PopOverContainerRootProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

/**
 * PopOverContainer — an elevated surface anchored to its trigger, holding whatever the caller puts
 * in it: a menu, a list, a picker.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=2521-5844
 */
function Root({ open, onOpenChange, children }: PopOverContainerRootProps) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </Popover.Root>
  )
}

/** Its child becomes the anchor, so it must forward `ref` and spread the props it is given. */
function Trigger({ children }: { children: React.ReactNode }) {
  return <Popover.Trigger asChild>{children}</Popover.Trigger>
}

export interface PopOverContainerContentProps extends Pick<
  React.ComponentProps<typeof Popover.Content>,
  'side' | 'sideOffset' | 'align' | 'aria-label' | 'onCloseAutoFocus'
> {
  /** Sizes the surface, which otherwise grows with its content. Not for border or shadow. */
  className?: string
  children: React.ReactNode
}

function Content({
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  className,
  children,
  ...props
}: PopOverContainerContentProps) {
  return (
    <Popover.Portal>
      <Popover.Content {...props} side={side} sideOffset={sideOffset} align={align}>
        <div className={tw(surfaceClasses, className)}>{children}</div>
      </Popover.Content>
    </Popover.Portal>
  )
}

export const PopOverContainer = {
  Root,
  Trigger,
  Content,
}
